import { Action, Dispatch, Middleware } from 'redux'

export type Listener = (action: Action) => void

type ActionListenerContainer = {
  action: string
  listener: Listener
}

const subscribedBefore: Listener[] = []
const subscribedAfter: Listener[] = []
const actionsSubscribedBefore: ActionListenerContainer[] = []
const actionsSubscribedAfter: ActionListenerContainer[] = []

const subscribe = (listener: Listener, listenerContainer: Listener[]) => {
  if (typeof listener !== 'function') {
    throw new Error('Expected the listener to be a function.')
  }
  listenerContainer.push(listener)
  return () => {
    const index = listenerContainer.indexOf(listener)
    index !== -1 && listenerContainer.splice(index, 1)
  }
}

export const subscribeBefore = (listener: Listener) => {
  return subscribe(listener, subscribedBefore)
}

export const subscribeAfter = (listener: Listener) => {
  return subscribe(listener, subscribedAfter)
}

const subscribeAction = (
  actionListenerContainer: ActionListenerContainer,
  listenerContainer: ActionListenerContainer[],
) => {
  if (!actionListenerContainer.action) {
    throw new Error('Expected the action to be a string.')
  }
  if (typeof actionListenerContainer.listener !== 'function') {
    throw new Error('Expected the listener to be a function.')
  }
  listenerContainer.push(actionListenerContainer)
  return () => {
    const index = listenerContainer.indexOf(actionListenerContainer)
    listenerContainer.splice(index, 1)
  }
}

export const subscribeActionBefore = (action: string, listener: Listener) => {
  const actionListenerContainer = { action, listener }
  return subscribeAction(actionListenerContainer, actionsSubscribedBefore)
}

export const subscribeActionAfter = (action: string, listener: Listener) => {
  const actionListenerContainer = { action, listener }
  return subscribeAction(actionListenerContainer, actionsSubscribedAfter)
}

export const unsubscribeBefore = () => {
  subscribedBefore.length = 0
}

export const unsubscribeActionsBefore = () => {
  actionsSubscribedBefore.length = 0
}

export const unsubscribeAfter = () => {
  subscribedAfter.length = 0
}

export const unsubscribeActionsAfter = () => {
  actionsSubscribedAfter.length = 0
}

export const unsubscribeAll = () => {
  unsubscribeBefore()
  unsubscribeActionsBefore()
  unsubscribeAfter()
  unsubscribeActionsAfter()
}

const unsubscribeAction = (
  listenerContainer: ActionListenerContainer[],
  filterAction: string,
) => {
  const filteredListenerContainer = listenerContainer.filter(
    ({ action }) => action !== filterAction,
  )
  listenerContainer.length = 0
  listenerContainer.concat(filteredListenerContainer)
}

export const unsubscribeActionBefore = (action: string) => {
  unsubscribeAction(actionsSubscribedBefore, action)
}

export const unsubscribeActionAfter = (action: string) => {
  unsubscribeAction(actionsSubscribedAfter, action)
}

export const unsubscribeActionAll = (action: string) => {
  unsubscribeActionBefore(action)
  unsubscribeActionAfter(action)
}

const callListeners = (action: Action, listenerContainer: Listener[]) => {
  for (let i = listenerContainer.length - 1; i >= 0; i--) {
    const listener = listenerContainer[i]
    if (typeof action === 'object') {
      listener(action)
    }
  }
}

const callActionListeners = (
  action: Action,
  listenerContainer: ActionListenerContainer[],
) => {
  for (let i = listenerContainer.length - 1; i >= 0; i--) {
    const listener = listenerContainer[i]
    if (typeof action === 'object' && listener.action === action.type) {
      listener.listener(action)
    }
  }
}

export const subscribeActionMiddleware: Middleware =
  () =>
  (next: Dispatch) =>
  <A extends Action>(action: A) => {
    callListeners(action, subscribedBefore)
    callActionListeners(action, actionsSubscribedBefore)
    const result = next(action)
    callListeners(action, subscribedAfter)
    callActionListeners(action, actionsSubscribedAfter)
    return result
  }
